import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import http from 'Utils/http';
import { cachedGet } from 'Utils/cachedGet';
import { Grid, Box, Typography, Button, MenuItem, Select, FormControl } from '@mui/material';
import { PaginationBar } from 'Shared/PaginationBar';
import { useSnackbar } from 'notistack';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { AddIcon, WhiteAddIcon } from 'Shared/Icons';
import { setFlow } from 'Actions/flow';
import { setLoading } from 'Actions/loading';
import { MyFlowsItem } from 'Components/shared/flows/MyFlowsItem';
import { CustomSnackbar } from 'Shared/CustomSnackbar';
import { UserPreDefinedFlowsItem } from 'Components/shared/flows/UserPreDefinedFlowsItem';
import FlowCreateForm from 'Shared/flows/flows-interface/FlowCreateForm';
import FlowExportFlow from 'Shared/flows/flows-interface/FlowExportFlow';
import FlowBulkExport from 'Shared/flows/flows-interface/FlowBulkExport';
import FlowBulkExportBar from 'Shared/flows/flows-interface/FlowBulkExportBar';
import FlowSelectAll from 'Shared/flows/flows-interface/FlowSelectAll';
import AiPromptDialog from 'Shared/flows/flows-interface/AiPromptDialog';
import FoldersSidebar from 'Shared/flows/folders/FoldersSidebar';
import CreateFolderModal from 'Shared/flows/folders/CreateFolderModal';
import { useFolders } from 'Shared/flows/folders/useFolders';
import { getFlowFolder, getFlowFolderPath } from 'Shared/flows/folders/folderUtils';
import { useFoundCounts } from 'Shared/flows/folders/useFoundCounts';
import FlowSearch from 'Shared/flows/folders/FlowSearch';
import { useFlowSelection } from 'Hooks/useFlowSelection';
import { InputLabel } from '@mui/material';
import config from '../../config';

const MyFlowsPage = () => {
    const dispatch = useDispatch();
    const { enqueueSnackbar } = useSnackbar();
    const currentUser = useSelector((state) => state.auth.user);
    const users = useSelector((state) => state.users?.users || []);

    const [flowsList, setFlowsList] = useState([]);
    const [userFlowsList, setUserFlowsList] = useState([]);
    const [visibleCreateFlowForm, setVisibleCreateFlowForm] = useState(false);
    const [needUpdateList, setNeedUpdateList] = useState(false);
    const [isImportFlow, setIsImportFlow] = useState(false);
    const [isAiPromptDialog, setIsAiPromptDialog] = useState(false);
    const [isBulkExport, setIsBulkExport] = useState(false);
    const { selectedFlows, isSelected, toggleFlow, removeFlow, toggleVisible, clearSelection } = useFlowSelection();
    const [selectedUserId, setSelectedUserId] = useState(currentUser?.id || '');
    // Pagination / applied search live in the URL, so they survive navigating
    // into a flow and back (and page refresh). URL is the source of truth.
    const [searchParams, setSearchParams] = useSearchParams();

    const myFlowsPage = parseInt(searchParams.get('myPage') || '0', 10);
    const myFlowsRowsPerPage = parseInt(searchParams.get('myRows') || '10', 10);
    const predefFlowsPage = parseInt(searchParams.get('predefPage') || '0', 10);
    const predefFlowsRowsPerPage = parseInt(searchParams.get('predefRows') || '10', 10);
    const appliedSearch = searchParams.get('search') || '';

    const [searchText, setSearchText] = useState(appliedSearch);
    const [myFlowsTotal, setMyFlowsTotal] = useState(0);
    const [predefFlowsTotal, setPredefFlowsTotal] = useState(0);
    const {
        folders, fetchFolders, view, folderId, selectedFolder, isSearchAll, listFolder,
        modal, openCreateFolder, openRenameFolder, closeFolderModal,
    } = useFolders();

    const foundCounts = useFoundCounts(`/user/flow/list?user_id=${selectedUserId}`, isSearchAll ? appliedSearch : '', folders);

    const updateParams = useCallback((updates) => {
        setSearchParams((prev) => {
            const next = new URLSearchParams(prev);
            Object.entries(updates).forEach(([key, value]) => {
                if (value === '' || value === null || value === undefined) {
                    next.delete(key);
                } else {
                    next.set(key, String(value));
                }
            });
            return next;
        }, { replace: true });
    }, [setSearchParams]);

    // The view and the folder go into the URL together with the reset paging:
    // a single update, otherwise the last one would overwrite the others.
    const selectView = useCallback((v) => {
        updateParams({ view: v, folder: null, myPage: 0 });
    }, [updateParams]);

    const selectFolder = useCallback((id) => {
        updateParams({ view: 'folders', folder: id, myPage: 0 });
    }, [updateParams]);

    // Keep the search input in sync with the URL (so the FLOWS menu link, which
    // drops the query, also clears the input; browser Back restores it).
    useEffect(() => setSearchText(appliedSearch), [appliedSearch]);

    const toCreateFlow = (e) => {
        e.preventDefault();
        setVisibleCreateFlowForm(true);
    };

    const getListsFlows = useCallback(() => {
        dispatch(setLoading(true));
        const predefPagination = JSON.stringify({ pageSize: predefFlowsRowsPerPage, currentPage: predefFlowsPage });
        const myPagination = JSON.stringify({ pageSize: myFlowsRowsPerPage, currentPage: myFlowsPage });
        const searchParam = appliedSearch ? `&search=${encodeURIComponent(appliedSearch)}` : '';
        axios.all([
            cachedGet(`/flows?active_for_company_only=1&pagination=${encodeURIComponent(predefPagination)}${searchParam}`),
            cachedGet(`/user/flow/list?user_id=${selectedUserId}&pagination=${encodeURIComponent(myPagination)}${searchParam}&folder_id=${encodeURIComponent(listFolder)}`)
        ]).then(
            axios.spread((firstResponse, secondResponse) => {
                const flowsArray = Array.isArray(firstResponse.data.flows)
                    ? firstResponse.data.flows
                    : Object.values(firstResponse.data.flows || {});
                setFlowsList(flowsArray);
                setPredefFlowsTotal(firstResponse.data.total ?? flowsArray.length);
                setUserFlowsList(secondResponse.data.flows);
                setMyFlowsTotal(secondResponse.data.total ?? secondResponse.data.flows.length);
                dispatch(setLoading(false));
            })
        ).catch((err) => {
            dispatch(setLoading(false));
            enqueueSnackbar(`${err.response.data.message}`, {
                action: CustomSnackbar,
                variant: 'error'
            });
        });
    }, [dispatch, enqueueSnackbar, selectedUserId, predefFlowsPage, predefFlowsRowsPerPage, myFlowsPage, myFlowsRowsPerPage, appliedSearch, listFolder]);

    const getMyFlowsList = useCallback(() => {
        const pagination = JSON.stringify({ pageSize: myFlowsRowsPerPage, currentPage: myFlowsPage });
        const searchParam = appliedSearch ? `&search=${encodeURIComponent(appliedSearch)}` : '';
        cachedGet(`/user/flow/list?pagination=${encodeURIComponent(pagination)}${searchParam}&folder_id=${encodeURIComponent(listFolder)}`).then((res) => {
            dispatch(setFlow(res.data.flows));
            setUserFlowsList(res.data.flows);
            setMyFlowsTotal(res.data.total ?? res.data.flows.length);
            setNeedUpdateList(false);
        }).catch(() => {});
    }, [dispatch, setUserFlowsList, setNeedUpdateList, myFlowsPage, myFlowsRowsPerPage, appliedSearch, listFolder]);

    useEffect(() => {
        if (needUpdateList) {
            getMyFlowsList()
        }
    }, [needUpdateList, getMyFlowsList]);

    const linkFlowToUser = (item) => {
        dispatch(setLoading(true));
        http.post(`/user/flow/${item.id}/copy`).then(() => {
            getMyFlowsList();
            dispatch(setLoading(false));
            enqueueSnackbar('Flow was added to your list', {
                action: CustomSnackbar,
                variant: 'success'
            });
        }).catch((err) => {
            dispatch(setLoading(false));
            enqueueSnackbar(`${err.response.data.message}`, {
                action: CustomSnackbar,
                variant: 'error'
            });
        });
    };

    const deleteFlow = (item) => {
        dispatch(setLoading(true));
        http.delete(`/flow/${item.id}/delete`).then(() => {
            getMyFlowsList();
            dispatch(setLoading(false));
            enqueueSnackbar('Flow was deleted', {
                action: CustomSnackbar,
                variant: 'success'
            });
        }).catch((err) => {
            dispatch(setLoading(false));
            enqueueSnackbar(`${err.response.data.message}`, {
                action: CustomSnackbar,
                variant: 'error'
            });
        });
    };

    const unlinkFlow = (item) => {
        dispatch(setLoading(true));
        http.delete(`/flow/${item.id}/delete`).then(() => {
            getMyFlowsList();
            dispatch(setLoading(false));
            enqueueSnackbar('Flow was deleted', {
                action: CustomSnackbar,
                variant: 'success'
            });
        }).catch((err) => {
            dispatch(setLoading(false));
            enqueueSnackbar(`${err.response.data.message}`, {
                action: CustomSnackbar,
                variant: 'error'
            });
        });
    };


    const handleClear = () => {
        setSearchText('');
        updateParams({ myPage: 0, predefPage: 0, search: '' });
    };

    const findFlows = (text) => {
        updateParams({ view: 'all', folder: null, myPage: 0, predefPage: 0, search: text });
    };

    const openFoundFlow = (flow) => {
        const folder = getFlowFolder(folders, flow.id);
        updateParams({
            view: folder ? 'folders' : 'all',
            folder: folder ? folder.id : null,
            myPage: 0,
            predefPage: 0,
            search: flow.name,
        });
    };

    useEffect(() => {
        getListsFlows();
    }, [selectedUserId, predefFlowsPage, predefFlowsRowsPerPage, myFlowsPage, myFlowsRowsPerPage, appliedSearch, listFolder]);

    return (
        <>
            <FlowExportFlow visible={isImportFlow} setVisible={setIsImportFlow} update={setNeedUpdateList} folderId={selectedFolder === 'all' ? null : selectedFolder} onFoldersChanged={fetchFolders}/>
            <FlowBulkExport
                visible={isBulkExport}
                setVisible={setIsBulkExport}
                flows={selectedFlows}
                onRemove={removeFlow}
                onExported={clearSelection}
            />
            <AiPromptDialog visible={isAiPromptDialog} setVisible={setIsAiPromptDialog}/>
            <CreateFolderModal
                open={modal.open}
                onClose={closeFolderModal}
                folders={folders}
                defaultParentId={modal.parentId}
                editFolder={modal.editFolder}
                onSaved={fetchFolders}
            />
            {visibleCreateFlowForm && (
                <FlowCreateForm
                    visibleCreateFlowForm={visibleCreateFlowForm}
                    setVisibleCreateFlowForm={setVisibleCreateFlowForm}
                />
            )}
            <Grid container sx={{height: '100%'}}>
                <Grid item
                      xs={12}
                      md={4}
                      lg={3}
                      sx={{
                          paddingBottom: '20px',
                          '@media (min-width: 1024px)': {
                              paddingRight: '20px',
                              paddingBottom: '0',
                          },
                      }}>
                    <Box
                        sx={{
                            height: 'calc(100vh - 30px)',
                            backgroundColor: '#f9f7f5',
                            border: '1px solid #FFFFFF',
                            boxShadow: '1px 3px 15px rgba(0, 0, 0, 0.1)',
                            borderRadius: '16px',
                            overflow: 'hidden',
                            padding: '20px',
                            position: 'sticky',
                            top: '15px'
                        }}
                    >
                        <FoldersSidebar
                            folders={folders}
                            view={view}
                            selectedFolderId={folderId}
                            onSelectView={selectView}
                            onSelectFolder={selectFolder}
                            onFoldersChanged={fetchFolders}
                            onFlowChanged={getListsFlows}
                            onCreateChild={(folder) => openCreateFolder(folder.id)}
                            onRename={openRenameFolder}
                            foundCounts={foundCounts}
                        />
                    </Box>
                </Grid>

                <Grid item xs={12} md={8} lg={9}>
                    <Box
                        sx={{
                            height: '100%',
                            padding: '20px',
                            backgroundColor: '#FAFEFF',
                            border: '1px solid #FFFFFF',
                            boxShadow: '1px 3px 15px rgba(0, 0, 0, 0.1)',
                            borderRadius: '16px',
                            overflow: 'hidden',
                        }}
                    >
                        <Box sx={{ display: 'flex', gap: '10px', alignItems: 'center', marginBottom: '20px', justifyContent: 'flex-end' }}>
                            <FlowSearch
                                value={searchText}
                                onChange={setSearchText}
                                onSelect={openFoundFlow}
                                onFind={findFlows}
                                folders={folders}
                                sources={['/flows?active_for_company_only=1', `/user/flow/list?user_id=${selectedUserId}`]}
                            />
                            <Button variant="outlined" onClick={handleClear}>Clear</Button>
                        </Box>

                        <Box sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            marginBottom: '20px',
                            flexWrap: 'wrap',
                            gap: '10px'
                        }}>
                            <Typography
                                variant="h2"
                                color="primary.blackText"
                                sx={{
                                    fontWeight: 500,
                                    fontSize: '20px',
                                    lineHeight: '40px',
                                }}
                            >
                                My Active Flows:
                            </Typography>
                            <Box sx={{
                                display: 'flex',
                                gap: '10px',
                                flexWrap: 'wrap'
                            }}>
                                <Button
                                    onClick={() => openCreateFolder()}
                                    startIcon={<WhiteAddIcon/>}
                                    variant="contained"
                                >
                                    Create Folder
                                </Button>
                                <Button
                                    onClick={() => setIsImportFlow(true)}
                                    variant="contained"
                                >
                                    Import Flow
                                </Button>
                                <FlowBulkExportBar
                                    selectedFlows={selectedFlows}
                                    clearSelection={clearSelection}
                                    onExport={() => setIsBulkExport(true)}
                                />
                                {config.AI_ENABLED && (
                                    <Button
                                        onClick={() => setIsAiPromptDialog(true)}
                                        variant="contained"
                                        color="secondary"
                                    >
                                        AI Flow Builder
                                    </Button>
                                )}
                                <Button
                                    onClick={(e) => {
                                        toCreateFlow(e);
                                    }}
                                    startIcon={<AddIcon/>}
                                    variant="outlined"
                                >
                                    Create New Flow
                                </Button>
                            </Box>
                        </Box>

                        {!userFlowsList.length && (
                            <Typography
                                sx={{
                                    marginBottom: '20px',
                                    fontWeight: 500,
                                    fontSize: '20px',
                                    lineHeight: '24px',
                                    color: '#D76A6A',
                                }}
                            >
                                Your flows list is empty
                            </Typography>
                        )}

                        {/* EDIT ITEM */}

                        <FlowSelectAll
                            items={userFlowsList}
                            selectedFlows={selectedFlows}
                            toggleVisible={toggleVisible}
                        />
                        {userFlowsList.map((item) => (
                            <MyFlowsItem
                                key={`${item.name}-${item.id}`}
                                item={item}
                                unlinkFlow={() => unlinkFlow(item)}
                                deleteFlow={deleteFlow}
                                needUpdate={setNeedUpdateList}
                                userRole={currentUser?.access}
                                selected={isSelected(item.id)}
                                onToggleSelect={toggleFlow}
                                folderPath={getFlowFolderPath(folders, item.id)}
                            />
                        ))}

                        {myFlowsTotal > 0 && (
                            <PaginationBar
                                count={myFlowsTotal}
                                page={myFlowsPage}
                                onPageChange={(e, newPage) => updateParams({ myPage: newPage })}
                                rowsPerPage={myFlowsRowsPerPage}
                                onRowsPerPageChange={(e) => {
                                    updateParams({ myRows: parseInt(e.target.value, 10), myPage: 0 });
                                }}
                            />
                        )}

                        {(currentUser?.access === 'Admin' || currentUser?.access === 'Company Admin') && (
                          <>
                            <Typography
                                variant="h2"
                                color="primary.blackText"
                                sx={{
                                    marginTop: '46px',
                                    marginBottom: '20px',
                                    textAlign: 'center',
                                    fontWeight: 500,
                                    fontSize: '20px',
                                    lineHeight: '40px',
                                }}
                            >
                                User's Flows:
                            </Typography>
                            <FormControl fullWidth sx={{ marginBottom: 2 }}>
                                <InputLabel id="select-user-label">Select User</InputLabel>
                                <Select
                                    labelId="select-user-label"
                                    value={selectedUserId}
                                    label="Select User"
                                    onChange={e => {
                                        setSelectedUserId(e.target.value);
                                    }}
                                >
                                    {users.map(user => (
                                        <MenuItem key={user.id} value={user.id}>{user.email}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                          </>
                        )}

                        {selectedFolder === 'all' && (
                          <>
                            <Typography
                                variant="h2"
                                color="primary.blackText"
                                sx={{
                                    marginTop: '46px',
                                    marginBottom: '20px',
                                    textAlign: 'center',
                                    fontWeight: 500,
                                    fontSize: '20px',
                                    lineHeight: '40px',
                                }}
                            >
                                Available Pre-Defined Flows:
                            </Typography>

                            <FlowSelectAll
                                items={flowsList}
                                selectedFlows={selectedFlows}
                                toggleVisible={toggleVisible}
                            />
                            {flowsList.map((item) => (
                                <UserPreDefinedFlowsItem
                                    key={item.name}
                                    item={item}
                                    linkFlowToUser={() => linkFlowToUser(item)}
                                    needUpdate={setNeedUpdateList}
                                    userRole={currentUser?.access}
                                    selected={isSelected(item.id)}
                                    onToggleSelect={toggleFlow}
                                />
                            ))}

                            {predefFlowsTotal > 0 && (
                                <PaginationBar
                                    count={predefFlowsTotal}
                                    page={predefFlowsPage}
                                    onPageChange={(e, newPage) => updateParams({ predefPage: newPage })}
                                    rowsPerPage={predefFlowsRowsPerPage}
                                    onRowsPerPageChange={(e) => {
                                        updateParams({ predefRows: parseInt(e.target.value, 10), predefPage: 0 });
                                    }}
                                />
                            )}
                          </>
                        )}
                    </Box>
                </Grid>
            </Grid>
        </>
    );
};

export default MyFlowsPage;
